import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { offeneKommentare, readLayout } from "@/office/excel/layout";
import { filledSheets, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionSchutz: Lesson = {
  id: "schutz",
  title: "Blattschutz und Zusammenarbeit",
  summary: "Nicht alles sperren, sondern das Gegenteil: freigeben, was ausgefüllt werden soll.",
  durationMinutes: 12,
  learningGoals: [
    "Zellen gezielt zur Bearbeitung freigeben und den Rest schützen",
    "Den Unterschied zwischen Blatt- und Mappenschutz kennen",
    "Kommentare für die Abstimmung nutzen",
  ],
  steps: [
    {
      id: "sperren-umgekehrt",
      title: "Der Blattschutz funktioniert andersherum, als man denkt",
      explanation: [
        "Jede Zelle in Excel trägt von Anfang an die Eigenschaft „Gesperrt“. Sie tut " +
          "nur nichts – solange das Blatt nicht geschützt ist, hat die Eigenschaft " +
          "keine Wirkung. Erst der Blattschutz aktiviert sie für alle Zellen " +
          "gleichzeitig.",
        "Daraus folgt die richtige Reihenfolge, und sie ist genau umgekehrt zur " +
          "Intuition. Man sperrt nicht, was geschützt sein soll. Man entsperrt, was " +
          "bearbeitbar bleiben soll – die Eingabezellen –, und schützt dann das Blatt. " +
          "Alles, was man nicht angefasst hat, ist damit automatisch dicht.",
        "Konkret: Eingabezellen markieren, Strg + 1, Registerkarte „Schutz“, Häkchen " +
          "bei „Gesperrt“ entfernen. Danach „Überprüfen“ → „Blatt schützen“. Im " +
          "Dialog lässt sich einstellen, was trotz Schutz erlaubt bleibt – Zellen " +
          "markieren, sortieren, filtern, AutoFilter benutzen.",
        "Ein Punkt, der oft übersehen wird: „Gesperrte Zellen auswählen“ auszuschalten, " +
          "ist mehr als Kosmetik. Der Cursor springt dann beim Tabulator direkt von " +
          "einem Eingabefeld zum nächsten und überspringt alles dazwischen. Aus einem " +
          "geschützten Blatt wird damit ein Formular, das sich bequem ausfüllen lässt.",
        "Und die Ehrlichkeit dazu: Das Passwort des Blattschutzes ist kein Schutz vor " +
          "Angriffen. Es ist schwach verschlüsselt und in Minuten zu umgehen. Der " +
          "Blattschutz verhindert Versehen – dass jemand versehentlich eine Formel " +
          "überschreibt –, nicht Absicht.",
      ],
      keyPoints: [
        "Erst die Eingabezellen entsperren, dann das Blatt schützen.",
        "Strg + 1 → Schutz → Häkchen „Gesperrt“ entfernen.",
        "„Gesperrte Zellen auswählen“ ausschalten macht daraus ein Formular.",
        "Das Passwort verhindert Versehen, nicht Absicht.",
      ],
      task: {
        instruction:
          "Schütze eines deiner Blätter – nachdem du die Zellen entsperrt hast, in " +
          "denen etwas eingetragen werden soll.",
        hint: "Erst Eingabezellen markieren und mit Strg + 1 → Schutz das Häkchen „Gesperrt“ entfernen. Dann Überprüfen → Blatt schützen.",
        check: async () => {
          const mappe = await readWorkbook();

          if (!mappe.features.sheetProtection) {
            return nichtPruefbar(
              "Der Schutzzustand eines Blattes",
              "1.2",
              "Sieh unter „Überprüfen“ nach: Steht dort „Blattschutz aufheben“, ist das Blatt geschützt.",
            );
          }

          const geschuetzt = mappe.sheets.filter((blatt) => blatt.isProtected === true);
          if (geschuetzt.length === 0) {
            const gefuellt = filledSheets(mappe);
            return fail(
              `Keines der ${mappe.sheets.length} Blätter ist geschützt.`,
              gefuellt.length > 0
                ? `Markiere auf „${gefuellt[0].name}“ zuerst die Eingabezellen und entferne mit ` +
                  "Strg + 1 → Schutz das Häkchen „Gesperrt“. Danach Überprüfen → Blatt schützen."
                : "Trage zuerst etwas ein, das sich zu schützen lohnt.",
            );
          }

          return pass(
            `${geschuetzt.length === 1 ? "Ein Blatt ist" : `${geschuetzt.length} Blätter sind`} geschützt: ` +
              `${geschuetzt.map((blatt) => `„${blatt.name}“`).join(", ")}. Probiere aus, ob sich deine ` +
              "Eingabezellen noch beschreiben lassen – sonst hast du sie vorher nicht entsperrt.",
          );
        },
      },
    },
    {
      id: "mappenschutz",
      title: "Blattschutz, Mappenschutz, Dateikennwort",
      explanation: [
        "Drei Schutzarten werden regelmäßig verwechselt, weil sie ähnlich heißen und " +
          "völlig Verschiedenes tun.",
        "Der Blattschutz sperrt Zellen gegen Änderung. Er gilt je Blatt und muss auf " +
          "jedem einzeln eingeschaltet werden – das wird gern vergessen, wenn eine " +
          "Datei später um Blätter wächst.",
        "Der Mappenschutz unter „Überprüfen“ → „Arbeitsmappe schützen“ tut etwas " +
          "ganz anderes: Er sperrt die Struktur. Blätter lassen sich dann nicht mehr " +
          "einfügen, löschen, umbenennen, verschieben oder ausblenden. An den " +
          "Zellinhalten ändert er nichts. Er ist die richtige Wahl, wenn Formeln über " +
          "Blattgrenzen greifen und ein gelöschtes Blatt alles zerstören würde.",
        "Das Dateikennwort schließlich – zu finden unter „Datei“ → „Informationen“ → " +
          "„Arbeitsmappe schützen“ → „Mit Kennwort verschlüsseln“ – verschlüsselt die " +
          "ganze Datei. Ohne Passwort lässt sie sich nicht öffnen. Das ist als " +
          "einziges der drei ein echter Schutz mit starker Verschlüsselung. Und es " +
          "ist unwiderruflich: Ein vergessenes Dateikennwort bedeutet, dass die Datei " +
          "weg ist.",
      ],
      keyPoints: [
        "Blattschutz = Zellen. Mappenschutz = Struktur. Dateikennwort = ganze Datei.",
        "Der Mappenschutz verhindert das Löschen und Umbenennen von Blättern.",
        "Nur das Dateikennwort ist echte Verschlüsselung – und unwiderruflich.",
      ],
      task: {
        instruction:
          "Schütze zusätzlich die Struktur deiner Arbeitsmappe, sodass sich Blätter " +
          "nicht mehr löschen oder umbenennen lassen.",
        hint: "Überprüfen → Arbeitsmappe schützen. Ein Passwort ist dabei optional.",
        check: async () => {
          const mappe = await readWorkbook();

          if (!mappe.features.workbookProtection) {
            return nichtPruefbar(
              "Der Schutzzustand der Arbeitsmappe",
              "1.7",
              "Sieh unter „Überprüfen“ nach, ob die Schaltfläche „Arbeitsmappe schützen“ hervorgehoben ist.",
            );
          }

          return fromCriteria(
            [
              criterion(
                mappe.isProtected === true
                  ? "Die Struktur der Arbeitsmappe ist geschützt"
                  : "Die Struktur der Arbeitsmappe ist noch nicht geschützt",
                mappe.isProtected === true,
              ),
              criterion(
                `Mindestens ein Blatt ist zusätzlich geschützt (gefunden: ` +
                  `${mappe.sheets.filter((blatt) => blatt.isProtected === true).length})`,
                mappe.sheets.some((blatt) => blatt.isProtected === true),
              ),
            ],
            {
              success:
                "Struktur und Zellinhalte sind getrennt geschützt – Blätter lassen sich nicht mehr " +
                "löschen, und die Formeln auf dem geschützten Blatt sind vor Versehen sicher.",
              failure: "Es fehlt noch eine der beiden Schutzarten.",
              hint: "Der Mappenschutz liegt unter Überprüfen → Arbeitsmappe schützen, der Blattschutz " +
                "unter Überprüfen → Blatt schützen. Beide sind unabhängig voneinander.",
            },
          );
        },
      },
    },
    {
      id: "kommentare",
      title: "Kommentare statt Zellen mit Notizen",
      explanation: [
        "Die schlechteste Art, eine Rückfrage in einer Tabelle zu hinterlassen, ist " +
          "eine Zelle daneben mit „stimmt das?“. Sie steht im Datenbereich, verfälscht " +
          "Auswertungen, wird beim Sortieren mitverschoben und bleibt am Ende " +
          "vergessen in der Datei stehen.",
        "Excel kennt dafür zwei Werkzeuge, die seit einigen Versionen " +
          "auseinandergehalten werden. Kommentare sind Unterhaltungen: Man schreibt " +
          "etwas, jemand anders antwortet darunter, und am Ende wird der Faden als " +
          "erledigt markiert. Sie sind das Werkzeug für die Abstimmung zu zweit.",
        "Notizen – früher hießen sie Kommentare – sind einzelne gelbe Zettel ohne " +
          "Antwortmöglichkeit. Sie eignen sich für dauerhafte Erläuterungen, die in " +
          "der Datei bleiben sollen: „Wert stammt aus dem Vorsystem, wird monatlich " +
          "überschrieben.“",
        "Beide hängen an der Zelle und wandern beim Sortieren mit ihr mit. Beim " +
          "Weitergeben einer Datei lohnt der Blick unter „Überprüfen“ → „Kommentare " +
          "anzeigen“: Ein offener Kommentar mit einer internen Rückfrage ist einer der " +
          "häufigsten Wege, auf dem Interna nach außen gelangen. Die " +
          "Dokumentprüfung, die das findet, ist Thema des letzten Moduls.",
      ],
      keyPoints: [
        "Kommentare sind Unterhaltungen mit Antworten, Notizen sind gelbe Zettel.",
        "Strg + Alt + M fügt einen neuen Kommentar ein.",
        "Vor dem Weitergeben: Überprüfen → Kommentare anzeigen und aufräumen.",
      ],
      task: {
        instruction:
          "Hinterlasse in deiner Arbeitsmappe einen Kommentar an einer Zelle – und " +
          "markiere ihn danach als erledigt, so wie du es vor der Weitergabe tun würdest.",
        hint: "Rechtsklick auf die Zelle → Neuer Kommentar (Strg + Alt + M). Erledigen über das Menü im Kommentarfenster.",
        check: async () => {
          const layout = await readLayout();

          if (layout.comments === null) {
            return nichtPruefbar(
              "Die Kommentare der Arbeitsmappe",
              "1.10",
              "Sieh unter „Überprüfen“ → „Kommentare anzeigen“ nach, welche Kommentare in der Datei stehen.",
              true,
            );
          }

          if (layout.comments.length === 0) {
            return fail(
              "In der Arbeitsmappe steht noch kein Kommentar.",
              "Rechtsklick auf eine Zelle → „Neuer Kommentar“, oder Strg + Alt + M.",
            );
          }

          const offen = offeneKommentare(layout);
          const erster = layout.comments[0];

          // Ob ein Kommentar als erledigt gilt, steht erst ab ExcelApi 1.11
          // bereit. Fehlt die Angabe, wird der zweite Teil der Aufgabe offen
          // benannt statt still übergangen.
          if (erster.resolved === null) {
            return pass(
              `${layout.comments.length} ${layout.comments.length === 1 ? "Kommentar" : "Kommentare"} ` +
                `gefunden, zum Beispiel „${erster.content.slice(0, 60)}“` +
                (erster.authorName ? ` von ${erster.authorName}` : "") +
                ". Ob ein Kommentar als erledigt markiert ist, lässt sich erst ab dem " +
                "Anforderungssatz ExcelApi 1.11 auslesen – diese Excel-Version meldet es nicht. " +
                "Prüfe den zweiten Teil der Aufgabe deshalb selbst im Kommentarfenster.",
            );
          }

          if (offen.length === layout.comments.length) {
            return fail(
              `Alle ${layout.comments.length} Kommentare sind noch offen, zum Beispiel ` +
                `„${erster.content.slice(0, 60)}“.`,
              "Öffne den Kommentar und wähle im Menü rechts oben „Thread auflösen“. Vor dem Weitergeben " +
                "einer Datei gehören offene Rückfragen aufgeräumt.",
            );
          }

          return pass(
            `${layout.comments.length - offen.length} von ${layout.comments.length} ` +
              `${layout.comments.length === 1 ? "Kommentar ist" : "Kommentaren sind"} als erledigt markiert.` +
              (offen.length > 0 ? ` ${offen.length} ${offen.length === 1 ? "bleibt" : "bleiben"} offen.` : ""),
          );
        },
      },
    },
  ],
};
